import { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from '@digdir/designsystemet-react';
import cn from 'classnames';

import { Routes } from '../../enums/Routes';
import { currentPage } from './currentPage';
import classes from './SidebarItem.module.css';

type SidebarGroupItemProps = {
  icon: ReactElement;
  label: string;
  pages: Array<{ route: Routes; label: string }>;
  onNavigate?: () => void;
  collapsed?: boolean;
};

/**
 * A menu item with pages under it. It links to the first page, and shows the pages while one
 * of them is open, or a page opened from one of them. That page is marked as current, so the
 * item itself is only marked in bold.
 */
export const SidebarGroupItem = ({
  icon,
  label,
  pages,
  onNavigate,
  collapsed = false,
}: SidebarGroupItemProps): ReactElement => {
  const { pathname } = useLocation();
  const current = currentPage(pages, pathname);
  const open = current !== null;
  const link = (
    <Link
      to={pages[0].route}
      onClick={onNavigate}
      className={cn('ds-focus', classes.item, {
        [classes.collapsed]: collapsed,
      })}
      data-open={open || undefined}
    >
      <span className={classes.icon}>{icon}</span>
      <span className={cn(classes.label, { 'ds-sr-only': collapsed })}>
        {label}
      </span>
    </Link>
  );

  return (
    <li>
      {collapsed ? (
        <Tooltip content={label} placement="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
      {open && !collapsed && (
        <ul className={classes.pages}>
          {pages.map((page) => (
            <li key={page.route}>
              <Link
                to={page.route}
                aria-current={page === current ? 'page' : undefined}
                onClick={onNavigate}
                className={cn('ds-focus', classes.page)}
              >
                {page.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};
